import { ErrorService } from './../../../../core/services/error.service';
import { CampaignService } from './../../../../core/services/api/campaign.service';
import { CampaignEventModel } from '../../../../core/models/campaign/campaign-event.model';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { PopupService } from './../../../../shared/services/popup/popup.service';
import { BlockUiService } from './../../../../shared/services/blockUi/block-ui.service';
import moment from 'moment';
import { StorageService } from './../../../../core/services/storage.service';
import { Role } from './../../../../core/enum/role.enum';
import {
  ImageFileFormat,
  DefaultURLPattern,
  DefaultEmailPattern,
} from '../../../../core/services/constant';
import { AccountService } from '../../../../core/services/api/account.service';
import { ContainerType } from '../../../../core/enum/container-type.enum';
import { FileValidationService } from '../../../../core/services/file-validation.service';
import { FileUploadOption } from '../../../../core/models/attachment/file-upload-option';
import { AttachmentInfo } from '../../../../core/models/attachment/attachment-info';
import { FormValidators } from '../../../../core/validator/form.validators';
import { GeminiImageService } from 'src/app/core/services/gemini-image.service';
import { environment } from 'src/environments/environment';
import { CompanyModel } from '../../../../core/models/company/company.model';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../core/services/toast.service';
import { CampaignStatus } from 'src/app/core/enum/campaign-status.enum';

@Component({
    selector: 'app-events',
    templateUrl: './events.component.html',
    styleUrls: ['./events.component.scss'],
    standalone: false
})
export class EventsComponent implements OnInit, OnDestroy {
    @Input() campaignId: number;
    @Input() isCreator: boolean;
    //FRC-495
    @Input() campaignStatus: CampaignStatus;
    @Output() onUpdate = new EventEmitter();

    events: CampaignEventModel[] = [];
    total: number = 0;
    params = {
        currentPage: 1,
        take: 10,
    };

    //FRC-497, FRC-502
    minStartDate: Date = new Date();
    minTime: string = '00:00';

    isLoading: boolean = false;
    isAddEvent: boolean = false;
    showFormError: boolean = false;
    form: UntypedFormGroup;
    eventTags: string[] = [];
    selectable = true;
    removable = true;
    addOnBlur = true;
    imageUrl: any;
    file: File;
    event: CampaignEventModel;
    showActions: boolean = this.storageService.role == Role.IssuerRep;
    accept: string = ImageFileFormat.toString();
    attachmentInfo: AttachmentInfo;
    isImageLoading = false;
    company: CompanyModel;

    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    readonly TIME_OFFSET_MINUTES = 1;
    useAutoGenerateZoomMeeting: boolean = false;
    useAIAutoGenerateImage: boolean = false;

    private destroy$ = new Subject<void>();
    CampaignStatus = CampaignStatus;

    constructor(
        private campaignService: CampaignService,
        private errorService: ErrorService,
        private formBuilder: UntypedFormBuilder,
        private popupService: PopupService,
        private blockUiService: BlockUiService,
        private storageService: StorageService,
        private accountService: AccountService,
        private fileValidationService: FileValidationService,
        private geminiImageService: GeminiImageService,
        private translateService: TranslateService,
        private toastService: ToastService,
    ) { }

    ngOnInit() {
        this.useAutoGenerateZoomMeeting = environment.flagToUseAutoGenerateZoomMeeting;
        this.useAIAutoGenerateImage = true;

        this.getEvents(true);
        this.getIssuer();

    }

    getEvents(reload?: boolean) {
        if (reload) {
            this.params.currentPage = 1;
            this.events = [];
        }

        this.isLoading = true;

        this.campaignService
            .getEvents(this.campaignId, this.params.currentPage, this.params.take)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (data) => {
                    this.events = this.events.concat(data.data);
                    this.events.forEach((event) => {
                        event.isOngoing = moment().isBetween(event.startDate, event.endDate);
                        event.isUpcoming = moment().isBefore(event.startDate);

                        if (event.eventTags) event.eventTagsArray = JSON.parse(event.eventTags);
                    });
                    this.total = data.total;
                },
                error: (err) => {
                    this.errorService.showError(err);
                },
            });
    }

    addEvent() {
        this.isAddEvent = !this.isAddEvent;

        if (this.isAddEvent) {
            this.buildForm();
            this.resetFormData();
        }
    }

    private parseDateInput(date: any): Date | null {
        if (!date) return null;
        if (moment.isMoment(date)) return date.clone().toDate();
        if (date instanceof Date) return date;
        if (typeof date === 'object' && date.year) return new Date(date.year, date.month - 1, date.day);
        if (typeof date === 'string') return new Date(date);
        return null;
    }

    private parseTimeInput(t: any): { h: number; m: number } | null {
        if (!t) return null;
        if (typeof t === 'string') {
            const m = t.match(/^(\d{1,2}):(\d{2})(?:\s*([AP]M))?$/i);
            if (!m) return null;
            let [_, hh, mm, ampm] = m;
            let h = +hh,
                m2 = +mm;
            if (ampm?.toUpperCase() === 'PM' && h < 12) h += 12;
            if (ampm?.toUpperCase() === 'AM' && h === 12) h = 0;
            return { h, m: m2 };
        }
        if (t.hour != null) return { h: t.hour, m: t.minute };
        return null;
    }

    private formatDateLine(date: any, includeYear = true): string {
        const d = this.parseDateInput(date);
        if (!d) return 'Date (Day)';
        const datePart = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        const dayPart  = d.toLocaleDateString('en-US', { weekday: 'short' });
        return includeYear ? `${datePart} ${d.getFullYear()} (${dayPart})` : `${datePart} (${dayPart})`;   
    }

    private formatTimeLabel(time: any): string {
        const t = this.parseTimeInput(time);
        if (!t) return 'Time';
        const d = new Date(2000, 0, 1, t.h, t.m);
        return d
            .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
            .replace('AM', 'am')
            .replace('PM', 'pm');
    }

    private sanitize(s: string): string {
        return (s ?? '').replace(/[\r\n"']+/g, '').trim();
    }

    // --- Load Poster Required Data ---
    private buildPosterPayload() {
      const v = this.form.value;
      return {
        titleLine: v.title ? `${this.sanitize(v.title)} by` : 'Pitch Session by',
        dateLine: this.formatDateLine(v.startDate),
        timeLine: this.formatTimeLabel(v.startTime),
        linkLine: v.eventUrlType === 'auto' ? 'Zoom' : (v.eventLink?.trim() || ''),
        companyLogoUrl: this.company?.companyLogoUrl ?? '',
        aspectRatio: '16:9' as const
      };
    }

    // --- Image helpers ---
    private async loadImage(rawUrl: string): Promise<HTMLImageElement> {
        const timestamp = Date.now();
        const proxyUrl = `${environment.apiServer}api/ImageGeneration/proxy-image?url=${encodeURIComponent(rawUrl)}`;
        const directUrl = `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}t=${timestamp}`;

        try {
            return await this.fetchImageAsBlob(proxyUrl);
        } catch (proxyError) {
            try {
                return await this.fetchImageAsBlob(directUrl);
            } catch (directError) {
                throw directError;
            }
        }
    }
    
    private async fetchImageAsBlob(url: string): Promise<HTMLImageElement> {
        const response = await fetch(url, { 
            method: 'GET',
            mode: 'cors',
            cache: 'no-store',
            credentials: 'omit'
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const originalBlob = await response.blob();
        if (originalBlob.size === 0) throw new Error('Empty Blob');

        let mimeType = originalBlob.type;
        if (!mimeType || mimeType === 'application/octet-stream') {
        console.warn(`[LogoStrategy] Server returned ${mimeType}, forcing 'image/png' to bypass browser security.`);
        mimeType = 'image/png'; 
        }

        const fixedBlob = new Blob([originalBlob], { type: mimeType });
        const objectUrl = URL.createObjectURL(fixedBlob);

        return await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(img);
            };
            img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Blob render failed'));
            };
            img.src = objectUrl;
        });
    }

    private async drawPosterCanvas(payload: {
      titleLine: string;
      dateLine: string;
      timeLine: string;
      linkLine: string;
      companyLogoUrl: string;
      aspectRatio: '16:9';
    }): Promise<HTMLCanvasElement> {
      const width = 1920, height = 1080;
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const marginX = Math.floor(width * 0.08);
      const safeWidth = width - marginX * 2;

      const yTitle      = Math.round(height * 0.18); 
      const yLogoCenter = Math.round(height * 0.42); 
      const yDate       = Math.round(height * 0.72); 
      const yTime       = Math.round(height * 0.82);

      const families = 'system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,"Noto Sans","PingFang SC","Microsoft YaHei",sans-serif';
      const drawText = (text: string, y: number, maxPx: number, minPx = 24, weight: number | string = 600) => {
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        let size = maxPx;
        while (size >= minPx) {
          ctx.font = `${weight} ${size}px ${families}`;
          if (ctx.measureText(text).width <= safeWidth) break;
          size -= 2;
        }
        let out = text;
        if (ctx.measureText(out).width > safeWidth) {
          while (out.length > 0 && ctx.measureText(out + '…').width > safeWidth) out = out.slice(0, -1);
          out += '…';
        }
        ctx.fillText(out, cx, y);
      };

      drawText(payload.titleLine, yTitle, 120, 28, 700);
      drawText(payload.dateLine,  yDate,   60, 26, 600);
      drawText(`${payload.timeLine} @ ${payload.linkLine}`, yTime, 52, 24, 600); 

      if (payload.companyLogoUrl) {
        try {
            const img = await this.loadImage(payload.companyLogoUrl);

            const maxLogoH = Math.round(height * 0.28);   
            const maxLogoW = Math.round(safeWidth * 0.60); 
            const scale    = Math.min(maxLogoW / img.width, maxLogoH / img.height, 1);
            const targetW  = Math.round(img.width  * scale);
            const targetH  = Math.round(img.height * scale);
            const dx       = Math.round(cx - targetW / 2);
            const dy       = Math.round(yLogoCenter - targetH / 2);

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, dx, dy, targetW, targetH);
        } catch (error) {
            console.error('Failed to load/draw company logo:', (error as any)?.message ?? error);
        }
      }

      return canvas;
    }


    // --- Main flow ---
    async autoGenerateImage() {
        this.isImageLoading = true;

        try {
            const payload = this.buildPosterPayload();
            const canvas = await this.drawPosterCanvas(payload);

            const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob((b) => resolve(b), 'image/png', 1)
            );
            if (!blob) {
            throw new Error('Failed to create blob from canvas');
            }

            const file = new File([blob], `event-${Date.now()}.png`, { type: 'image/png' });

            this.accountService
            .uploadAttachmentPresignedURL(file, ContainerType.CampaignEvent)
            .pipe(finalize(() => (this.isImageLoading = false)))
            .subscribe({
                next: (r) => {
                this.imageUrl = r.url;
                this.attachmentInfo = new AttachmentInfo(r);
                },
                error: (e) => {
                console.error(e);
                if (this.toastService?.warning) {
                    this.toastService.warning(this.translateService.instant('toast.imageUploadFailed'));
                } else {
                    this.popupService?.alert?.(this.translateService.instant('toast.imageUploadFailed'));
                }
                },
            });
        } catch (err) {
            console.error(err);
            if (this.toastService?.warning) {
            this.toastService.warning(this.translateService.instant('toast.failedToAutoGenerateImage'));
            } else {
            this.popupService?.alert?.(this.translateService.instant('toast.failedToAutoGenerateImage'));
            }
            this.isImageLoading = false;
        }
    }

    buildForm() {
        this.form = this.formBuilder.group({
            title: ['', Validators.required],
            eventDescription: [''],
            startDate: ['', Validators.required, FormValidators.minYearValidator(1990)],
            startTime: ['', Validators.required],
            endDate: [{ value: '', disabled: true }, Validators.required],
            endTime: [{ value: '', disabled: true }, Validators.required],
            eventLink: [''],
            imageType: ['custom'],
            issuerZoomEmail: [''],
            eventUrlType: ['custom'],
        });

        //FRC-497
        this.form.get('startDate').valueChanges.subscribe(() => {
            this.updateEndDateAndTimeControls();
            this.updateCurrentTime();
        });
        this.form.get('startTime').valueChanges.subscribe(() => {
            this.updateEndDateAndTimeControls();
        });
        this.form.get('endDate').valueChanges.subscribe(() => {
            this.updateEndDateAndTimeControls();
        });
    }

    //FRC-497
    private updateEndDateAndTimeControls() {
        const endDateControl = this.form.get('endDate');
        const endTimeControl = this.form.get('endTime');
        const startDateValue = this.form.get('startDate')?.value;
        const startTimeValue = this.form.get('startTime')?.value;
        const endDateValue = this.form.get('endDate')?.value;

        const isStartComplete = startDateValue && startTimeValue;

        if (isStartComplete) {
            if (endDateControl && endDateControl.disabled) {
                endDateControl.enable({ emitEvent: false });
            }
        } else {
            if (endDateControl && endDateControl.enabled) {
                endDateControl.disable({ emitEvent: false });
                endDateControl.setValue('', { emitEvent: false });
            }
        }

        if (endDateValue) {
            if (endTimeControl && endTimeControl.disabled) {
                endTimeControl.enable({ emitEvent: false });
            }
        } else {
            if (endTimeControl && endTimeControl.enabled) {
                endTimeControl.disable({ emitEvent: false });
                endTimeControl.setValue('', { emitEvent: false });
            }
        }
    }
    //FRC-497 TO SET THE TIME RESTRICTION IF DATE = TODAY
    private updateCurrentTime() {
        const startDateControl = this.form.get('startDate');
        const startTimeControl = this.form.get('startTime');

        if (!startDateControl || !startTimeControl) return;

        this.minStartDate = new Date();

        const selectedDate = startDateControl.value;
        const todayMoment = moment().startOf('day');

        const isToday = selectedDate && moment(selectedDate).startOf('day').isSame(todayMoment);

        if (isToday) {
            this.minTime = moment().add(this.TIME_OFFSET_MINUTES, 'minute').format('HH:mm');
        } else {
            this.minTime = '00:00';
        }
        startTimeControl.updateValueAndValidity();
    }

    get formControl() {
        return this.form.controls;
    }

    add(event: MatChipInputEvent): void {
        const input = event.input;
        const value = event.value;

        if ((value || '').trim()) this.eventTags.push(value.trim());

        if (input) input.value = '';
    }

    remove(tag: string): void {
        const index = this.eventTags.indexOf(tag);

        if (index >= 0) this.eventTags.splice(index, 1);
    }

    async selectFile(file: File) {
        const uploadOption: FileUploadOption = {
            fileType: ImageFileFormat,
            errorMsg: 'common.onlyImageAccepted',
        };

        if (await this.fileValidationService.validFile(file, uploadOption)) {
            this.file = file;
            this.isImageLoading = true;

            this.accountService
                .uploadAttachmentPresignedURL(file, ContainerType.CampaignEvent)
                .pipe(finalize(() => (this.isImageLoading = false)))
                .subscribe(
                    (data) => {
                        this.imageUrl = data.url;
                        this.attachmentInfo = new AttachmentInfo(data);
                    },
                    (err) => {
                        this.errorService.showError(err);
                        this.toastService.warning(this.translateService.instant('toast.imageUploadFailed'));
                        this.imageUrl = null;
                        this.attachmentInfo = null;
                    },
                );
        }
    }

    removeImage() {
        this.attachmentInfo = null;
        this.imageUrl = null;
    }

    submit() {
        if (this.form.invalid) {
            this.showFormError = true;
            return;
        }

        if (!this.attachmentInfo && !this.imageUrl) {
            const pleaseUploadEventImage = this.translateService.instant(
                'campaign.pleaseUploadEventImage',
            );
            this.showFormError = true;
            this.toastService.warning(pleaseUploadEventImage);
            return;
        }

        let startDate = moment(
            moment(this.form.value.startDate).format('YYYY-MM-DD') + ' ' + this.form.value.startTime,
            'YYYY-MM-DD hh:mm A',
        );
        let endDate = moment(
            moment(this.form.value.endDate).format('YYYY-MM-DD') + ' ' + this.form.value.endTime,
            'YYYY-MM-DD hh:mm A',
        );

        if (startDate.isAfter(endDate)) {
            const dateIncorrectMessage = this.translateService.instant('campaign.dateIncorrectMessage');
            this.toastService.warning(dateIncorrectMessage);
            return;
        }

        let event = this.form.value;
        event.eventTags = this.eventTags;
        event.attachmentInfo = this.attachmentInfo;
        event.startDate = startDate.toISOString();
        event.endDate = endDate.toISOString();

        if (this.useAutoGenerateZoomMeeting) {
            event.imageType = this.form.value.imageType;
            event.eventUrlType = this.form.value.eventUrlType;
        } else {
            event.imageType = 'custom';
            event.eventUrlType = 'custom';
        }

        let observable =
            this.event && this.event.id
                ? this.campaignService.udpateEvent(event, this.campaignId, this.event.id)
                : this.campaignService.addEvent(event, this.campaignId);

        this.blockUiService.open();

        observable.pipe(finalize(() => this.blockUiService.close())).subscribe({
            next: (res) => {
                if (this.event && this.event.id) {
                    const updateSuccess = this.translateService.instant('campaign.updateSuccess');
                    this.toastService.success(updateSuccess);
                } else {
                        const addSuccess = this.translateService.instant('campaign.addSuccess');
                        this.toastService.success(addSuccess);
                }

                this.isAddEvent = !this.isAddEvent;
                this.getEvents(true);
                this.getStatisticCount();
                this.onUpdate.emit();
            },
            error: (err) => this.errorService.showError(err),
        });
    }

    loadMore() {
        this.params.currentPage++;
        this.getEvents();
    }

    deleteEvent(id: number) {
        this.popupService.confirm('campaign.confirmDeleteEvent').then(
            () => {
                this.blockUiService.open();

                this.campaignService
                    .deleteEvent(id, this.campaignId)
                    .pipe(finalize(() => this.blockUiService.close()))
                    .subscribe({
                        next: () => {
                            //FRC-362
                            const deleteSuccess = this.translateService.instant('campaign.deleteSuccess');
                            this.toastService.success(deleteSuccess);

                            this.getEvents(true);
                            this.getStatisticCount();
                            this.onUpdate.emit();
                        },
                        error: (err) => this.errorService.showError(err),
                    });
            },
            () => {},
        );
    }

    editEvent(event: CampaignEventModel) {
        this.isAddEvent = !this.isAddEvent;
        this.event = event;
        this.buildForm();
        this.form.patchValue(event);
        this.form.controls['startDate'].setValue(moment(event.startDate));
        this.form.controls['endDate'].setValue(moment(event.endDate));
        this.form.controls['startTime'].setValue(moment(event.startDate).format('hh:mm a'));
        this.form.controls['endTime'].setValue(moment(event.endDate).format('hh:mm a'));
        this.imageUrl = event.photoAttachmentUrl;
        this.attachmentInfo = event.attachmentInfo;
        this.eventTags = event.eventTags ? JSON.parse(event.eventTags) : [];
    }

    cancel() {
        this.isAddEvent = !this.isAddEvent;
        this.resetFormData();
    }

    resetFormData() {
        this.showFormError = false;
        this.event = null;
        this.eventTags = [];
        this.file = null;
        this.attachmentInfo = null;
        this.imageUrl = null;
    }

    getStatisticCount() {
        this.campaignService.getStatisticCount(this.campaignId).subscribe({
            next: (data) => this.campaignService.statisticCount.next(data),
            error: (err) => this.errorService.showError(err),
        });
    }

    getTags(tagList) {
        return tagList.map((tag) => {
            return {
                name: tag,
            };
        });
    }

    getIssuer() {
        this.accountService.getCompanyInfo().subscribe(
            (data) => {
                this.company = data;
            },
            (err) => {
                this.errorService.showError(err);
            },
        );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
